import type { SearchResponse, SearchResultDTO } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { CornerDownLeft, Search } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Kbd, Spinner } from '../components/ui/Spinner';
import { useFocusTrap, useScrollLock } from '../components/ui/overlay';
import { useAuth } from '../hooks/useAuth';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { isTypingTarget } from '../hooks/useHotkeys';
import { api, apiUrl, errorMessage } from '../lib/api';
import { buildPaletteGroups, type PaletteItem } from './commandPaletteModel';

export const SEARCH_DEBOUNCE_MS = 150;
const SEARCH_LIMIT = 8;
const NO_RESULTS: SearchResultDTO[] = [];

export type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Global search + navigation (⌘K / Ctrl+K anywhere, `/` when not typing). Mount once in the shell; it owns
 * the global shortcut. ↑/↓ move, Enter opens, Esc closes.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpenChange(!open);
      } else if (event.key === '/' && !open && !event.metaKey && !event.ctrlKey && !isTypingTarget(event.target)) {
        event.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;
  return <PaletteDialog onClose={() => onOpenChange(false)} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  useFocusTrap(panelRef, true);
  useScrollLock(true);

  const debounced = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const search = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => api.get<SearchResponse>(apiUrl('/api/search', { q: debounced, limit: SEARCH_LIMIT }), { signal }),
    enabled: debounced.length > 0,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });

  const results = (debounced.length > 0 ? search.data?.results : undefined) ?? NO_RESULTS;
  const groups = useMemo(() => buildPaletteGroups(query, results, can), [query, results, can]);
  const items = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const active = items[Math.min(activeIndex, items.length - 1)];

  // New results → start from the top.
  useEffect(() => setActiveIndex(0), [query, search.data]);

  useEffect(() => {
    if (!active) return;
    listRef.current?.querySelector(`[data-item-id="${active.id}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (item: PaletteItem) => {
    onClose();
    void navigate(item.to);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (items.length === 0) return;
      const current = Math.min(activeIndex, items.length - 1);
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current + delta + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active) choose(active);
    }
  };

  const searching = search.isFetching && debounced.length > 0;
  const pendingDebounce = query.trim() !== debounced;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
      <div aria-hidden className="fixed inset-0 animate-fade-in bg-slate-900/40 dark:bg-black/60" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search and commands"
        onKeyDown={onKeyDown}
        className="relative flex max-h-[min(34rem,76vh)] w-full max-w-xl animate-pop-in flex-col overflow-hidden rounded-xl border border-slate-200 bg-surface shadow-panel"
      >
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-4">
          <Search size={17} aria-hidden className="shrink-0 text-slate-400" />
          <input
            data-autofocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads, contacts, notes… or type a command"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active ? `${listId}-${active.id}` : undefined}
            aria-label="Search"
            className="h-12 flex-1 bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          {(searching || pendingDebounce) && query.trim() !== '' && <Spinner size={15} className="text-slate-400" />}
          <Kbd>Esc</Kbd>
        </div>

        <div ref={listRef} id={listId} role="listbox" aria-label="Results" className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {search.isError && (
            <p className="px-3 py-2 text-[13px] text-red-600" role="alert">
              Search failed: {errorMessage(search.error)}
            </p>
          )}
          {groups.map((group) => (
            <div key={group.key} role="group" aria-label={group.label} className="mb-1 last:mb-0">
              <div className="px-2.5 pt-2 pb-1 text-2xs font-semibold tracking-wide text-slate-400 uppercase">{group.label}</div>
              {group.items.map((item) => {
                const index = items.indexOf(item);
                const isActive = item === active;
                const Icon = item.icon;
                return (
                  <div
                    key={item.id}
                    id={`${listId}-${item.id}`}
                    data-item-id={item.id}
                    role="option"
                    aria-selected={isActive}
                    onMouseMove={() => index !== activeIndex && setActiveIndex(index)}
                    onClick={() => choose(item)}
                    className={clsx(
                      'flex h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 text-[13px]',
                      isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-700',
                    )}
                  >
                    <Icon size={16} aria-hidden className={isActive ? 'text-ember-600' : 'text-slate-400'} />
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium">{item.label}</span>
                      {item.sublabel && <span className="ml-2 text-slate-500">{item.sublabel}</span>}
                    </span>
                    {item.hint && <span className="text-2xs tracking-wider text-slate-400">{item.hint}</span>}
                    {isActive && <CornerDownLeft size={14} aria-hidden className="text-slate-400" />}
                  </div>
                );
              })}
            </div>
          ))}
          {items.length === 0 && !searching && !pendingDebounce && (
            <p className="px-3 py-8 text-center text-[13px] text-slate-500">No results for “{query.trim()}”</p>
          )}
        </div>

        <div className="flex items-center gap-4 border-t border-slate-100 bg-slate-50/70 px-4 py-2 text-2xs text-slate-500">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </span>
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd> open
          </span>
          <span className="ml-auto hidden items-center gap-1 sm:flex">
            <Kbd>G</Kbd> then <Kbd>L</Kbd> leads
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
